// EditTools: 비디오 편집 도구의 메인 컨트롤러 컴포넌트
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CutMenu from './cut/CutMenu';
import ConcatMenu from './concat/ConcatMenu';
import WatermarkMenu from './watermark/WatermarkMenu';
import CropMenu from './crop/CropMenu';
import InfoCardMenu from './info/InfoCardMenu';
import { setCutlist, setWatermarkList, setCropList, setConcatList, clearEditHistory, setProjectData, setEditingState } from '@modules/editor';
import api from '@utils/axiosInstance';
import { milliSecondsToFormattedTime } from '@utils/milliSecondsToFormattedTime';
import EditToolMenuList from './EditToolMenuList';
import EditToolActionButtons from './EditToolActionButtons';
import { toast } from 'react-toastify';

function EditTools({ videoDurationTime, setWatermarkCanvasOpen, setCropCanvasOpen, setChangingList }: any) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { uuid, edit_id } = useParams();
    const { t } = useTranslation();
    const organizationData = useSelector((state: any) => state.organization.organizationData);
    const userToken = useSelector((state: any) => state.organization.userToken);
    const cutList = useSelector((state: any) => state.editor.cutList);
    const watermarkList = useSelector((state: any) => state.editor.watermarkList);
    const cropList = useSelector((state: any) => state.editor.cropList);
    const concatList = useSelector((state: any) => state.editor.concatList);
    const projectData = useSelector((state: any) => state.editor.projectData);
    const [activeTool, setActiveTool] = useState('');
    const [hasSaved, setHasSaved] = useState<{ [key: string]: any }>({});
    const [menuEditing, setMenuEditing] = useState<{ [key: string]: any }>({});

    function getProjectDataByAction(action: any) {
        console.log('[DEBUG] [getProjectDataByAction] action', action, projectData);
        const projectDataItem = projectData && projectData.find((item: any) => item.action === action);
        return projectDataItem ? projectDataItem.data : null;
    }

    function restoreDataFromProjectData(action: any) {
        const projectDataItem = getProjectDataByAction(action);
        switch (action) {
            case 'cut':
                if (projectDataItem) {
                    const cutData = projectDataItem.map((cut: any) => {
                        const [hours, minutes, seconds] = cut.from.split(':').map(Number);
                        const from = hours * 3600 + minutes * 60 + seconds;
                        const [h2, m2, s2] = cut.to.split(':').map(Number);
                        const to = h2 * 3600 + m2 * 60 + s2;
                        return { from, to };
                    });
                    dispatch(setCutlist(cutData));
                } else {
                    dispatch(setCutlist([]));
                }
                break;
            case 'watermark':
                const allProjectWatermarks = projectData.filter((item: any) => item.action === 'watermark').map((item: any) => item.data);
                dispatch(setWatermarkList(allProjectWatermarks));
                break;
            case 'crop':
                if (projectDataItem) {
                    dispatch(setCropList(projectDataItem));
                } else {
                    dispatch(setCropList({}));
                }
                break;
            case 'concat':
                if (projectDataItem) {
                    dispatch(setConcatList(projectDataItem));
                } else {
                    dispatch(setConcatList({}));
                }
                break;
        }
    }

    function isToolValueChanged(currentData: any, projectDataItem: any, type: any) {
        if (!projectDataItem) {
            return currentData && (
                (Array.isArray(currentData) && currentData.length > 0) ||
                (typeof currentData === 'object' && Object.keys(currentData).length > 0)
            );
        }
        switch (type) {
            case 'cut':
                if (!Array.isArray(currentData)) return false;
                const projectCutData = projectDataItem.map((cut: any) => ({ from: cut.from, to: cut.to }));
                const currentCutData = currentData.map((cut: any) => ({ from: milliSecondsToFormattedTime(cut.from), to: milliSecondsToFormattedTime(cut.to) }));
                return JSON.stringify(currentCutData) !== JSON.stringify(projectCutData);
            case 'watermark':
                const allProjectWatermarks = projectData.filter((item: any) => item.action === 'watermark').map((item: any) => item.data);
                if (!Array.isArray(currentData)) return false;
                if (currentData.length === 0 && allProjectWatermarks.length > 0) return true;
                if (currentData.length > 0 && allProjectWatermarks.length === 0) return true;
                const currentWatermarkData = currentData.map((wm: any) => ({
                    type: wm.type,
                    x: parseInt(wm.x) || 0,
                    y: parseInt(wm.y) || 0,
                    width: parseInt(wm.width) || 0,
                    height: parseInt(wm.height) || 0,
                    text: wm.type === 'text' ? wm.text : undefined,
                    image: wm.type === 'image' ? wm.image : undefined,
                    color: wm.color,
                    fontSize: wm.fontSize
                }));
                return JSON.stringify(currentWatermarkData) !== JSON.stringify(allProjectWatermarks);
            case 'crop':
                if (!currentData || Object.keys(currentData).length === 0) {
                    return projectDataItem && Object.keys(projectDataItem).length > 0;
                }
                if (!projectDataItem || Object.keys(projectDataItem).length === 0) {
                    return Object.keys(currentData).length > 0;
                }
                const projectCropData = projectDataItem;
                const currentCropData = { x: currentData.x, y: currentData.y, width: currentData.width, height: currentData.height };
                return JSON.stringify(currentCropData) !== JSON.stringify(projectCropData);
            case 'concat':
                if (!currentData || Object.keys(currentData).length === 0) {
                    return projectDataItem && Object.keys(projectDataItem).length > 0;
                }
                if (!projectDataItem || Object.keys(projectDataItem).length === 0) {
                    return Object.keys(currentData).length > 0;
                }
                const projectConcatData = projectDataItem;
                const currentConcatData: any = {};
                if ((currentData as any).before) currentConcatData.before = { uuid: (currentData as any).before.uuid };
                if ((currentData as any).after) currentConcatData.after = { uuid: (currentData as any).after.uuid };
                return JSON.stringify(currentConcatData) !== JSON.stringify(projectConcatData);
            default:
                return false;
        }
    }

    useEffect(() => {
        if (activeTool) {
            setMenuEditing((prev: { [key: string]: any }) => ({ ...prev, [activeTool]: false }));
        }
    }, [activeTool]);

    useEffect(() => {
        const checkAndSetEditing = (tool: any, currentData: any) => {
            if (!tool) return;
            const projectDataItem = getProjectDataByAction(tool);
            if (isToolValueChanged(currentData, projectDataItem, tool)) {
                setMenuEditing((prev: { [key: string]: any }) => ({ ...prev, [tool]: true }));
            }
        };
        checkAndSetEditing('cut', cutList);
        checkAndSetEditing('concat', concatList);
        checkAndSetEditing('watermark', watermarkList);
        checkAndSetEditing('crop', cropList);
    }, [cutList, concatList, watermarkList, cropList, projectData]);

    const isEditing = !!activeTool && menuEditing[activeTool];

    useEffect(() => {
        setWatermarkCanvasOpen(activeTool === 'watermark');
        setCropCanvasOpen(activeTool === 'crop');
        if (activeTool) {
            restoreDataFromProjectData(activeTool);
        }
    }, [activeTool, setWatermarkCanvasOpen, setCropCanvasOpen]);

    useEffect(() => {
        if (activeTool === 'concat') {
            const projectDataItem = getProjectDataByAction('concat');
        }
    }, [activeTool]);

    function handleSave() {
        // 워터마크/크롭에서 하나라도 row의 isEditing이 true면 저장 막기
        if (
            (activeTool === 'watermark' && Array.isArray(watermarkList) && watermarkList.some(wm => wm.isEditing)) ||
            (activeTool === 'crop' && cropList && cropList.isEditing)
        ) {
            toast.error('편집을 완료해주세요', { position: 'top-center' });
            return;
        }
        const newProjectData = [...projectData];
        const currentToolName = activeTool;
        if (currentToolName === 'concat') {
            console.log('[DEBUG] [concat] 저장 전: concatList', concatList);
            console.log('[DEBUG] [concat] 저장 전: projectData', projectData);
        }
        switch (currentToolName) {
            case 'cut':
                if (cutList.length > 0) {
                    const cutData = cutList.map((cut: any) => ({ from: milliSecondsToFormattedTime(cut.from), to: milliSecondsToFormattedTime(cut.to) }));
                    const cutIndex = newProjectData.findIndex((item: any) => item.action === 'cut');
                    if (cutIndex !== -1) newProjectData.splice(cutIndex, 1);
                    newProjectData.push({ action: 'cut', data: cutData });
                }
                break;
            case 'watermark':
                const watermarkIndices: any[] = [];
                newProjectData.forEach((item: any, index: any) => { if (item.action === 'watermark') watermarkIndices.push(index); });
                watermarkIndices.reverse().forEach((index: any) => { newProjectData.splice(index, 1); });
                if (watermarkList.length > 0) {
                    watermarkList.forEach((watermark: any) => {
                        const watermarkData: any = {
                            type: watermark.type,
                            x: parseInt(watermark.x) || 0,
                            y: parseInt(watermark.y) || 0,
                            width: parseInt(watermark.width) || 0,
                            height: parseInt(watermark.height) || 0,
                            color: watermark.color,
                            fontSize: watermark.fontSize
                        };
                        if ((watermark as any).type === 'text') (watermarkData as any).text = (watermark as any).text || '';
                        else if ((watermark as any).type === 'image') (watermarkData as any).image = (watermark as any).image || '';
                        newProjectData.push({ action: 'watermark', data: watermarkData });
                    });
                }
                break;
            case 'crop':
                const cropIndex = newProjectData.findIndex((item: any) => item.action === 'crop');
                if (cropIndex !== -1) newProjectData.splice(cropIndex, 1);
                if (Object.keys(cropList).length > 0) {
                    const cropData = { x: cropList.x, y: cropList.y, width: cropList.width, height: cropList.height };
                    newProjectData.push({ action: 'crop', data: cropData });
                }
                break;
            case 'concat':
                const concatIndex = newProjectData.findIndex((item: any) => item.action === 'concat');
                if (concatIndex !== -1) newProjectData.splice(concatIndex, 1);
                if (concatList && ((concatList as any).before || (concatList as any).after)) {
                    const concatData: any = {};
                    if ((concatList as any).before) concatData.before = { uuid: (concatList as any).before.uuid };
                    if ((concatList as any).after) concatData.after = { uuid: (concatList as any).after.uuid };
                    newProjectData.push({ action: 'concat', data: concatData });
                }
                break;
        }
        dispatch(setProjectData(newProjectData));
        if (currentToolName === 'concat') {
            console.log('[DEBUG] [concat] 저장 후: projectData', newProjectData);
        }
        if (edit_id) {
            api({
                method: 'post',
                url: '/editor/update',
                data: {
                    tk: userToken,
                    uuid: uuid,
                    ogc: organizationData.organization_code,
                    project_id: edit_id,
                    projectData: newProjectData
                }
            })
                .then(() => { dispatch(setEditingState(currentToolName, false)); })
                .catch(() => { });
        } else {
            dispatch(setEditingState(currentToolName, false));
        }
        setHasSaved((prev: { [key: string]: any }) => ({ ...prev, [currentToolName]: true }));
        setMenuEditing((prev: { [key: string]: any }) => ({ ...prev, [currentToolName]: false }));
        setActiveTool('');
    }

    function resetEditList() {
        const currentToolName = activeTool;
        if (currentToolName) {
            if (currentToolName === 'concat') {
                const projectDataItem = getProjectDataByAction('concat');
            }
            restoreDataFromProjectData(currentToolName);
            dispatch(clearEditHistory());
            dispatch(setEditingState(currentToolName, false));
            setMenuEditing((prev: { [key: string]: any }) => ({ ...prev, [currentToolName]: false }));
            if (currentToolName === 'concat') {
                const projectDataItem = getProjectDataByAction('concat');
                if (projectDataItem) {
                    dispatch(setConcatList(projectDataItem));
                } else {
                    dispatch(setConcatList({}));
                }
            }
        }
        setActiveTool('');
    }

    function RenderMenus() {
        switch (activeTool) {
            case 'cut': return <CutMenu setChangingList={setChangingList} videoDurationTime={videoDurationTime} />;
            case 'concat': return <ConcatMenu />;
            case 'watermark': return <WatermarkMenu />;
            case 'crop': return <CropMenu />;
            case 'info': return <InfoCardMenu />;
            default: return null;
        }
    }

    return (
        <>
            <EditToolActionButtons
                activeEditTool={!!activeTool}
                isEditing={isEditing}
                setEditTool={() => setActiveTool('')}
                resetEditList={resetEditList}
                handleSave={handleSave}
                uuid={uuid}
                t={t}
            />
            <EditToolMenuList
                activeTool={activeTool}
                setEditTool={setActiveTool}
                hasSaved={hasSaved}
                t={t}
            />
            <RenderMenus />
        </>
    );
}

export default EditTools;
